import { plain } from "@/components/dock/DockSheet";

// Android Chrome (and iPhone Safari with the Contact Picker feature flag on) can open the phone book
export const canPickContacts = () => typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;

export const pickFromPhone = async () => {
  const supported = await navigator.contacts.getProperties().catch(() => ["name", "tel", "email"]);
  const props = ["name", "tel", "email", "address"].filter((p) => supported.includes(p));
  const list = await navigator.contacts.select(props, { multiple: true });
  return list.map((c) => {
    const a = (c.address || [])[0] || {};
    return { name: (c.name || [])[0] || "", phone: (c.tel || [])[0] || "", email: (c.email || [])[0] || "", city: a.city || "", country: a.country || "" };
  }).filter((p) => p.name || p.phone || p.email);
};

const unescape = (s) => s.replace(/\\n/gi, " ").replace(/\\([,;\\])/g, "$1").trim();
const qp = (s) => {
  try { return decodeURIComponent(s.replace(/=\r?\n/g, "").replace(/=([0-9A-F]{2})/gi, "%$1")); } catch { return s; }
};

// Reads the .vcf file the iPhone (or any phone) Contacts app shares
export const parseVcf = (text) => {
  const lines = text.replace(/\r?\n[ \t]/g, "").split(/\r?\n/);
  const out = [];
  let cur = null;
  for (const line of lines) {
    const i = line.indexOf(":");
    if (i < 0) continue;
    const [head, ...params] = line.slice(0, i).split(";");
    const key = head.split(".").pop().toUpperCase();
    let val = line.slice(i + 1);
    if (params.some((p) => /QUOTED-PRINTABLE/i.test(p))) val = qp(val);
    if (key === "BEGIN") cur = { name: "", n: "", phone: "", email: "", city: "", country: "" };
    else if (key === "END" && cur) {
      const name = cur.name || cur.n;
      if (name || cur.phone || cur.email) out.push({ name, phone: cur.phone, email: cur.email, city: cur.city, country: cur.country });
      cur = null;
    } else if (cur) {
      if (key === "FN") cur.name = unescape(val);
      else if (key === "N") { const [last = "", first = ""] = val.split(";").map(unescape); cur.n = `${first} ${last}`.trim(); }
      else if (key === "TEL" && !cur.phone) cur.phone = val.replace(/^tel:/i, "").trim();
      else if (key === "EMAIL" && !cur.email) cur.email = val.trim();
      else if (key === "ADR" && !cur.city && !cur.country) { const parts = val.split(";").map(unescape); cur.city = parts[3] || ""; cur.country = parts[6] || ""; }
    }
  }
  return out;
};

const ALIAS = {
  "united states": "United States of America", usa: "United States of America", us: "United States of America", america: "United States of America",
  uk: "United Kingdom", "great britain": "United Kingdom", england: "United Kingdom", scotland: "United Kingdom", wales: "United Kingdom",
  "czech republic": "Czechia", uae: "United Arab Emirates", holland: "Netherlands", "the netherlands": "Netherlands",
  "bosnia and herzegovina": "Bosnia and Herz.", "bosnia & herzegovina": "Bosnia and Herz.", "south korea": "South Korea", korea: "South Korea",
  "dominican republic": "Dominican Rep.", "central african republic": "Central African Rep.", "democratic republic of the congo": "Dem. Rep. Congo",
  "ivory coast": "Côte d'Ivoire", "cote d'ivoire": "Côte d'Ivoire", "cote d’ivoire": "Côte d'Ivoire", "north macedonia": "Macedonia", turkiye: "Turkey",
  "congo - kinshasa": "Dem. Rep. Congo", "congo - brazzaville": "Congo", "myanmar (burma)": "Myanmar",
};
const region = typeof Intl !== "undefined" && Intl.DisplayNames ? new Intl.DisplayNames(["en"], { type: "region" }) : null;

// Turns 'DE', 'Deutschland'-style codes or 'USA' into the map's country name when we can
export const toCountry = (raw, names) => {
  let s = (raw || "").trim();
  if (!s) return "";
  if (/^[A-Za-z]{2}$/.test(s) && region) { try { s = region.of(s.toUpperCase()) || s; } catch { /* not a region code */ } }
  const t = plain(s);
  const hit = names.find((n) => plain(n) === t) || names.find((n) => plain(n) === plain(ALIAS[t] || ""));
  return hit || "";
};

// Last 9 digits, same rule as the server, so '+49 151 234' and '0151 234' line up
export const phoneKey = (p) => { const d = (p || "").replace(/\D/g, ""); return d.length >= 7 ? d.slice(-9) : ""; };
const keysOf = (p) => [p.email && `e:${p.email.trim().toLowerCase()}`, phoneKey(p.phone) && `p:${phoneKey(p.phone)}`, phoneKey(p.whatsapp) && `p:${phoneKey(p.whatsapp)}`].filter(Boolean);

// Drops people already saved (same phone or email) and repeats inside the import itself
export const dropKnown = (list, wishlist) => {
  const seen = new Set((wishlist || []).flatMap((w) => (w.contacts || []).flatMap(keysOf)));
  const fresh = list.filter((p) => {
    const k = keysOf(p);
    if (k.some((x) => seen.has(x))) return false;
    k.forEach((x) => seen.add(x));
    return true;
  });
  return { fresh, skipped: list.length - fresh.length };
};

export const matchKeys = keysOf;
